// ============================================================
// firebase.js — Configuração do Firebase e acesso ao Firestore
//
// Config, instâncias (db, auth, functions) e as funções que leem/
// gravam dados (dispensas, atletas, membros, núcleos). Extraído do
// App.js (Fase 1, Parte B) sem alterar nenhuma lógica.
// ============================================================

import { initializeApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  deleteDoc,
  getCountFromServer,
} from "firebase/firestore";
import { criarCache, intervaloDoMes } from "./leituras";
import { normalizarModalidade } from "./calculo";
import { firebaseConfig, AMBIENTE, EH_PRODUCAO } from "./ambiente.gerado";

// ── Firebase ────────────────────────────────────────────────
// A config não mora aqui. Ela vem de ambiente.gerado.js, que o script
// ambientes/gerar-config-web.js escreve a partir do registro da plataforma,
// para o ambiente declarado no build.
//
// Se aquele script não rodou, este import falha e o build morre — que é
// exatamente o comportamento desejado. Não existe config padrão e, em
// especial, não existe queda silenciosa para produção: um painel de
// desenvolvimento apontando sem avisar para o banco dos atletas é o
// acidente que a separação de ambientes existe para impedir.
export { AMBIENTE, EH_PRODUCAO };

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const auth = getAuth(app);
export const functions = getFunctions(app);
export const storage = getStorage(app);

// Define o nível de acesso ao painel a partir do campo "perfil" do users/{uid}.
// admin               → vê todos os núcleos (seletor livre, cria núcleos)
// coordenador_geral    → travado no próprio núcleo, cria coordenador técnico
// coordenador_tecnico  → travado no próprio núcleo (funções de sempre)
// tecnico              → travado no próprio núcleo E na própria modalidade,
//                         só leitura (presença/falta/classificação — Fase 4)
// sem_acesso          → recusado (atleta, auxiliar técnico, etc.)
//
// "geral" sozinho NÃO identifica admin (evita que "Coordenador Geral" seja
// promovido a admin por engano — Fase 3). Admin é detectado só por "admin".
//
// O nível "tecnico" aceita qualquer perfil que CONTENHA "técnico" (ex:
// "Técnico", "Técnico Esportivo", "Técnico de BMX"), desde que não seja
// "Auxiliar Técnico" nem "Coordenador Técnico" (esses já foram capturados
// pelos ramos acima, mas a exclusão explícita evita qualquer ambiguidade).
export function classificarAcesso(perfil) {
  const p = (perfil || "").toLowerCase();
  const pSemAcento = (perfil || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  if (p.includes("admin")) return "admin";
  // Hierarquia de coordenação (ordem CRÍTICA — todos contêm "coorden"):
  //  1. "Coordenador Geral" → vê TODOS os núcleos (como admin, sem criar núcleo)
  //  2. "Coordenador Técnico" → só o núcleo dele; NÃO cria outro Coord. Técnico
  //  3. "Coordenador" (puro) → só o núcleo dele; cria até Coord. Técnico
  if (p.includes("coorden") && p.includes("geral")) return "coordenador_geral";
  if (pSemAcento.includes("coorden") && pSemAcento.includes("tecnico")) return "coordenador_tecnico";
  if (p.includes("coorden")) return "coordenador";
  // Preparação física (Preparador Físico, Auxiliar de Preparação): veem
  // TODOS os atletas do núcleo (todas as modalidades), só leitura. Detecta
  // "prepar" — cobre preparador, preparadora e "Auxiliar de Preparação".
  // Vem ANTES do ramo de auxiliar/monitor, senão "Auxiliar de Preparação"
  // (que contém "auxiliar") cairia como auxiliar técnico por engano.
  if (pSemAcento.includes("prepar")) return "preparador";
  // Auxiliar/Monitor técnico: vê só a MODALIDADE dele, meses anteriores,
  // SEM comparativo. Precisa vir ANTES do ramo "tecnico" genérico, porque
  // "Auxiliar Técnico"/"Monitor Técnico" contêm "tecnico".
  if (pSemAcento.includes("monitor") || pSemAcento.includes("auxiliar")) return "auxiliar";
  // Técnico pleno: qualquer perfil com "técnico" que NÃO seja monitor/
  // auxiliar/coordenador. Vê a modalidade dele + comparativo dela.
  if (pSemAcento.includes("tecnico")) return "tecnico";
  // Atleta: acessa o portal só para acompanhar a PRÓPRIA frequência (só
  // leitura dos próprios dados — garantido pelas regras do Firestore).
  if (pSemAcento.includes("atleta")) return "atleta";
  return "sem_acesso";
}

// ── Leitura de presença: só o período que a tela mostra ──────
//
// Seis telas faziam a mesma coisa: baixavam o histórico inteiro do núcleo e
// filtravam o mês no navegador. Em setembro de 2026 eram 2.499 documentos
// lidos para mostrar 434, e a diferença crescia todo mês, porque o
// histórico cresce e o mês não. O painel respondia por 99% das leituras do
// projeto, com média de 37 mil por dia e pico de 147 mil, contra uma cota
// gratuita de 50 mil.
//
// A consulta filtra por dateKey, que todo registro tem: os 2.499 de
// produção foram conferidos um a um, e as quatro gravações possíveis
// (aplicativo, portal, equipe e aprovação de solicitação) escrevem o campo.
// O índice composto (nucleo, dateKey) já existia.
const cacheDeLeitura = criarCache();

export async function carregarPresencasDoPeriodo(nucleo, deISO, ateISO) {
  if (!nucleo || !deISO || !ateISO) return [];
  return cacheDeLeitura.obter(`presencas|${nucleo}|${deISO}|${ateISO}`, async () => {
    const q = query(
      collection(db, "attendance_logs"),
      where("nucleo", "==", nucleo),
      where("dateKey", ">=", deISO),
      where("dateKey", "<=", ateISO),
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => d.data());
  });
}

// mes é 0-11, como nos seletores das telas.
export async function carregarPresencasDoMes(nucleo, mes, ano) {
  const periodo = intervaloDoMes(mes, ano);
  if (!periodo) return [];
  return carregarPresencasDoPeriodo(nucleo, periodo.de, periodo.ate);
}

// Chamado depois de gravar presença. Sem isto, quem registra e volta para a
// tela vê a lista de antes e acha que o registro não entrou.
export function esquecerLeiturasDePresenca() {
  cacheDeLeitura.invalidar();
}

// ── Acesso ao Firestore: dispensas e atletas ─────────────────
// Coleção "dispensas": cada doc tem data, tipo, subtipo, modalidade,
// nucleo, nome, motivo. O isolamento por núcleo é feito no filtro.
//
// Guardada por um minuto: Início, Relatório e Presença pedem a mesma lista
// em sequência, e trocar de aba relia tudo.
//
// A lista INTEIRA continua existindo para a tela de datas especiais, que
// gerencia o histórico e precisa dele. As telas de frequência usam a versão
// por período, abaixo.
export async function carregarDispensas(nucleo) {
  if (!nucleo) return [];
  return cacheDeLeitura.obter(`dispensas|${nucleo}`, async () => {
    const q = query(collection(db, "dispensas"), where("nucleo", "==", nucleo));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  });
}

// As dispensas do período, e não as 450 do núcleo inteiro.
//
// Mesma história da presença: a tela mostra um mês e recebia o histórico
// completo para filtrar no navegador. Em setembro de 2026 eram 450
// documentos para usar 131, e as dispensas se acumulam mês a mês como os
// registros. O índice (nucleo, data) foi criado para esta consulta.
//
// Dispensa sem o campo data não entra, e é o certo: sem data ela não afeta
// mês nenhum, e o cálculo de frequência já a descartava.
export async function carregarDispensasDoPeriodo(nucleo, deISO, ateISO) {
  if (!nucleo || !deISO || !ateISO) return [];
  return cacheDeLeitura.obter(`dispensas|${nucleo}|${deISO}|${ateISO}`, async () => {
    const q = query(
      collection(db, "dispensas"),
      where("nucleo", "==", nucleo),
      where("data", ">=", deISO),
      where("data", "<=", ateISO),
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  });
}

// mes é 0-11, como nos seletores das telas.
export async function carregarDispensasDoMes(nucleo, mes, ano) {
  const periodo = intervaloDoMes(mes, ano);
  if (!periodo) return [];
  return carregarDispensasDoPeriodo(nucleo, periodo.de, periodo.ate);
}

// ── Área do Atleta (só leitura dos próprios dados) ───────────
// Registros de presença do atleta logado. A consulta filtra por userId
// (== uid), e a regra do Firestore garante no servidor que ele NÃO
// consegue ler registros de outra pessoa mesmo que altere a query.
export async function carregarMeusRegistros(uid) {
  const q = query(collection(db, "attendance_logs"), where("userId", "==", uid));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// Dispensas que afetam o atleta: coletivas do núcleo, da modalidade dele,
// e as individuais dele (pelo nome). A regra do Firestore permite ao
// atleta ler coletivas/modalidade e as individuais com o próprio nome —
// individuais de terceiros (com dado sensível) ficam bloqueadas.
// Coletivas e de modalidade não têm dado pessoal de ninguém.
// Dispensas que afetam o atleta. O Firestore exige que a QUERY case com o
// que a regra permite (senão rejeita a query inteira por segurança). Por
// isso não dá pra pedir "todas do núcleo" e filtrar no cliente — fazemos
// 3 consultas específicas, cada uma alinhada com a regra:
//   1. coletivas do núcleo   2. da modalidade do atleta   3. individuais
//   com o nome dele. Depois juntamos, sem duplicar.
export async function carregarMinhasDispensas(nucleo, modalidade, nome) {
  const base = collection(db, "dispensas");
  const consultas = [
    query(base, where("nucleo", "==", nucleo), where("tipo", "==", "COLETIVA")),
    query(base, where("nucleo", "==", nucleo), where("tipo", "==", "MODALIDADE"), where("modalidade", "==", modalidade)),
    query(base, where("nucleo", "==", nucleo), where("tipo", "==", "INDIVIDUAL"), where("nome", "==", nome)),
  ];
  const resultados = await Promise.all(consultas.map(async c => {
    try { return (await getDocs(c)).docs; } catch { return []; }
  }));
  const vistos = new Set();
  const todas = [];
  resultados.flat().forEach(d => {
    if (vistos.has(d.id)) return;
    vistos.add(d.id);
    todas.push({ id: d.id, ...d.data() });
  });
  return todas;
}

// Lê os atletas de um núcleo a partir da coleção "users".
// Retorna { nome, modalidade } para popular o seletor. Só perfis de atleta.
// Mapa { emailMinusculo: { nome, ativo, inativadoEm } } com o nome ATUAL do
// cadastro, o status e a data de inativação de cada usuário do núcleo. Usado
// pelos relatórios para: (1) exibir o nome completo; (2) marcar inativos;
// (3) cortar o denominador do atleta inativado na data em que saiu.
export async function carregarNomesAtuais(nucleo) {
  if (!nucleo) return {};
  return cacheDeLeitura.obter(`nomes|${nucleo}`, () => buscarNomesAtuais(nucleo));
}

async function buscarNomesAtuais(nucleo) {
  const q = query(collection(db, "users"), where("nucleo", "==", nucleo));
  const snap = await getDocs(q);
  const mapa = {};
  snap.docs.forEach(docSnap => {
    const u = docSnap.data();
    const email = (u.email || "").trim().toLowerCase();
    if (!email || !u.name) return;
    // inativadoEm pode ser Timestamp do Firestore -> converte para ISO "aaaa-mm-dd".
    let inativadoEm = null;
    if (u.ativo === false && u.inativadoEm) {
      try {
        const d = u.inativadoEm.toDate ? u.inativadoEm.toDate() : new Date(u.inativadoEm);
        if (!isNaN(d.getTime())) {
          inativadoEm = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split("T")[0];
        }
      } catch (_) { /* sem data válida */ }
    }
    mapa[email] = { nome: u.name, ativo: u.ativo !== false, inativadoEm };
  });
  return mapa;
}

export async function carregarAtletas(nucleo) {
  const q = query(collection(db, "users"), where("nucleo", "==", nucleo));
  const snap = await getDocs(q);
  const pessoas = [];
  snap.docs.forEach(docSnap => {
    const u = docSnap.data();
    const perfil = (u.perfil || "").toLowerCase();
    const perfilSemAcento = (u.perfil || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    if (!u.name) return;
    // Atletas + equipe técnica (técnicos, monitores, preparadores,
    // auxiliares) — ambos podem receber dispensa INDIVIDUAL, e o cálculo
    // já aplica por nome pra qualquer um. Coordenação fica fora (não entra
    // no quadro de presença).
    const ehAtleta = perfil.includes("atleta");
    const ehEquipe = !perfil.includes("coorden") && !perfil.includes("admin") &&
      (perfilSemAcento.includes("tecnico") || perfilSemAcento.includes("monitor") || perfilSemAcento.includes("preparador") || perfilSemAcento.includes("preparadora"));
    if (!ehAtleta && !ehEquipe) return;
    // uid e email vêm junto: o uid é o ID do documento e é o que a regra do
    // Firestore usa para o atleta enxergar só o próprio treino físico.
    // Sem ele, a única âncora seria o e-mail, que muda no cadastro.
    // O inativo NÃO é filtrado aqui de propósito: quem já tem registro no
    // mês precisa continuar aparecendo na grade, porque a presença dele
    // aconteceu. Quem decide exibir é a tela, que cruza este `ativo` com a
    // existência de registros — ver SecaoPresencaFisica.
    pessoas.push({
      uid: docSnap.id,
      email: (u.email || "").trim().toLowerCase(),
      nome: u.name,
      modalidade: normalizarModalidade(u.modalidade),
      grupo: ehAtleta ? "ATLETA" : "EQUIPE",
      ativo: u.ativo !== false,
    });
  });
  // ordena: atletas primeiro, depois equipe técnica; alfabético dentro de cada grupo; sem duplicar
  const vistos = new Set();
  return pessoas
    .filter(a => { const k = a.nome + "|" + a.modalidade; if (vistos.has(k)) return false; vistos.add(k); return true; })
    .sort((a, b) => {
      if (a.grupo !== b.grupo) return a.grupo === "ATLETA" ? -1 : 1;
      return a.nome.localeCompare(b.nome);
    });
}

// Carrega TODOS os membros do núcleo (não só atletas) com dados completos,
// para a tela de gerenciamento (inclui inativos, uid, email, perfil, ativo).
export async function carregarMembrosGestao(nucleo) {
  const q = query(collection(db, "users"), where("nucleo", "==", nucleo));
  const snap = await getDocs(q);
  const membros = [];
  snap.docs.forEach(docSnap => {
    const u = docSnap.data();
    // Admin não é "membro do núcleo" para efeito de gestão — mesmo que a
    // conta tenha um campo nucleo residual, ela não deve aparecer aqui.
    if ((u.perfil || "").toLowerCase().includes("admin")) return;
    membros.push({
      uid: docSnap.id,
      nome: u.name || "(sem nome)",
      email: u.email || "",
      perfil: u.perfil || "",
      modalidade: u.modalidade || "",
      ativo: u.ativo !== false,
      // Campos do cadastro esportivo. A IDADE não vem daqui: é calculada na
      // tela a partir da data, porque idade gravada envelhece errado.
      dataNascimento: u.dataNascimento || "",
      categoria: u.categoria || "",
      // O sexo entra porque TODA equação de composição corporal e de
      // maturação é específica por sexo: rodar a equação masculina numa
      // atleta não devolve um resultado aproximado, devolve um resultado sem
      // sentido. Vem vazio enquanto o campo não existir no cadastro, e a
      // ficha cobra o campo em vez de calcular com um padrão.
      sexo: u.sexo || "",
      // CPF do próprio atleta, que é outro dado e outra pessoa que o CPF do
      // responsável. Veio da lista de bolsas, que a coordenação mantinha fora
      // do sistema e redigitava a cada prestação de contas.
      cpf: u.cpf || "",
      // Ranking da federação. Não sai dos resultados do programa, que só
      // enxerga os próprios atletas, então é digitado e anda com a data em
      // que foi consultado.
      rankings: Array.isArray(u.rankings) ? u.rankings : [],
      rankingEstadual: u.rankingEstadual || "",
      rankingNacional: u.rankingNacional || "",
      rankingAtualizadoEm: u.rankingAtualizadoEm || "",
      // Marca que existe foto de cadastro, sem trazer a foto: a lista tem 34
      // atletas e baixar 34 imagens para montar a tela seria lento à toa. A
      // imagem vem por atleta, em `obterFotoDeCadastro`.
      temFotoCadastro: Boolean(u.fotoCadastro && u.fotoCadastro.caminho),
      fotoCadastroEm: u.fotoCadastro && u.fotoCadastro.atualizadoEm
        ? u.fotoCadastro.atualizadoEm.toDate().toISOString() : "",
      // Dados do responsável legal, que o termo de consentimento exige.
      // Esta lista só é montada para a coordenação.
      responsavelNome: u.responsavelNome || "",
      responsavelCpf: u.responsavelCpf || "",
      responsavelParentesco: u.responsavelParentesco || "",
      responsavelTelefone: u.responsavelTelefone || "",
    });
  });
  return membros.sort((a, b) => {
    // ativos primeiro, depois por nome
    if (a.ativo !== b.ativo) return a.ativo ? -1 : 1;
    return a.nome.localeCompare(b.nome);
  });
}

// Grava (cria ou atualiza) uma dispensa no Firestore. Usa o id como ID do doc.
export async function salvarDispensa(disp) {
  const id = String(disp.id);
  await setDoc(doc(db, "dispensas", id), {
    data: disp.data,
    tipo: disp.tipo,
    subtipo: disp.subtipo || "",
    modalidade: disp.modalidade || "Todas",
    nucleo: disp.nucleo,
    nome: disp.nome || "",
    motivo: disp.motivo || "",
    criadoEm: serverTimestamp(),
  }, { merge: true });
  // Feriado cadastrado tem que aparecer na frequência no mesmo instante:
  // é a tela de datas especiais que muda o denominador das outras.
  esquecerLeiturasDePresenca();
}

// Remove uma dispensa do Firestore pelo id do documento.
export async function removerDispensa(id) {
  await deleteDoc(doc(db, "dispensas", String(id)));
  esquecerLeiturasDePresenca();
}

// ── Núcleos (Fase 2) ──────────────────────────────────────────
// Lê todos os núcleos cadastrados na coleção "nucleos". Ordena por
// nome no cliente (a coleção tende a ser pequena — poucos núcleos).
export async function carregarNucleos() {
  const snap = await getDocs(collection(db, "nucleos"));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.nome || a.id).localeCompare(b.nome || b.id));
}

// Lê as modalidades cadastradas de UM núcleo específico, direto do doc
// nucleos/{nucleoId}. Fonte única para os seletores de modalidade em
// "Gerenciar Atletas" e "Dispensas e Datas Especiais" — substitui a
// derivação antiga a partir dos atletas já cadastrados (Fase 2, passo 4).
export async function carregarModalidadesDoNucleo(nucleoId) {
  if (!nucleoId) return [];
  const snap = await getDoc(doc(db, "nucleos", nucleoId));
  if (!snap.exists()) return [];
  return snap.data().modalidades || [];
}

// O cadastro da modalidade de um núcleo: as modalidades e as categorias de
// cada uma. Uma leitura só, porque as duas coisas moram no mesmo documento e
// as telas que precisam de uma precisam da outra.
export async function carregarCadastroDoNucleo(nucleoId) {
  const vazio = { modalidades: [], categoriasPorModalidade: {}, instituicao: "", canalDeDireitos: "" };
  if (!nucleoId) return vazio;
  const snap = await getDoc(doc(db, "nucleos", nucleoId));
  if (!snap.exists()) return vazio;
  const d = snap.data();
  return {
    modalidades: d.modalidades || [],
    categoriasPorModalidade: d.categoriasPorModalidade || {},
    instituicao: d.instituicao || "",
    canalDeDireitos: d.canalDeDireitos || "",
  };
}

// Cria ou edita um núcleo via Cloud Function "gerenciarNucleo" (só admin).
// A ordenação alfabética das modalidades e toda a validação de permissão
// acontecem no servidor — aqui só repassa o que o admin preencheu na tela.
// { acao: "criar" | "editar", nucleoId, modalidades: string[], ativo? }
// endereco/lat/lng/raioMetros são opcionais: quando não vão na chamada, a
// function preserva o que já estava gravado (é um merge).
//
// Os campos ausentes são omitidos do payload em vez de irem como undefined,
// porque no transporte do callable undefined pode virar null — e null, para
// lat/lng, é justamente o comando de APAGAR a localização. Sem esse cuidado,
// o botão de ativar/inativar (que reusa esta função) zeraria as coordenadas
// do núcleo a cada clique.
export async function salvarNucleo({
  acao, nucleoId, modalidades, ativo, locais, nome,
  podioPorModalidade, categoriasPorModalidade,
  instituicao, canalDeDireitos,
}) {
  const payload = { acao, nucleoId, modalidades };
  if (ativo !== undefined) payload.ativo = ativo;
  if (locais !== undefined) payload.locais = locais;
  if (nome !== undefined) payload.nome = nome;
  // Os dois campos por modalidade.
  //
  // Faltavam aqui, e esta função é um FUNIL: o que ela não desmonta do
  // objeto não chega ao servidor. A tela mandava os dois, o servidor sabia
  // recebê-los, e eles eram descartados no meio do caminho, em silêncio.
  //
  // Quem cadastrava categoria via a tela salvar com sucesso e voltar
  // vazia. E o pódio de cada modalidade nunca saiu do padrão, por isso o
  // quadro do BMX mostrava três colunas onde premia cinco.
  //
  // Ausente continua querendo dizer "não mexi nisto": o servidor preserva
  // o que está gravado, e é o que permite inativar um núcleo sem levar
  // junto as categorias de todas as modalidades.
  if (podioPorModalidade !== undefined) payload.podioPorModalidade = podioPorModalidade;
  if (categoriasPorModalidade !== undefined) payload.categoriasPorModalidade = categoriasPorModalidade;
  // A controladora dos dados, pela mesma razão do funil acima: o termo de
  // consentimento nomeia a instituição responsável, e ela é por núcleo.
  if (instituicao !== undefined) payload.instituicao = instituicao;
  if (canalDeDireitos !== undefined) payload.canalDeDireitos = canalDeDireitos;

  const chamar = httpsCallable(functions, "gerenciarNucleo");
  const resultado = await chamar(payload);
  return resultado.data;
}

// O ranking da federação de um ano, para um atleta.
//
// Manda um ANO de cada vez, e não a lista inteira: duas pessoas lançando
// anos diferentes ao mesmo tempo não se sobrescrevem. Devolve o histórico
// inteiro já normalizado pelo servidor, que é o que a tela passa a mostrar.
export async function salvarRankingDoAtleta({ atletaUid, ano, estadual, nacional }) {
  const chamar = httpsCallable(functions, "salvarRankingDoAtleta");
  const r = await chamar({ atletaUid, ano, estadual, nacional });
  return r.data;
}

// Exclusão de núcleo. Só o administrador, e a function recusa se ainda
// houver usuário, presença, dispensa, solicitação ou treino externo ligado
// — apagar deixaria tudo isso órfão. A confirmação repete o identificador
// para o servidor conferir que não foi clique errado.
export async function excluirNucleo(nucleoId, confirmacao, forcar = false) {
  const chamar = httpsCallable(functions, "excluirNucleo");
  const r = await chamar({ nucleoId, confirmacao, forcar });
  return r.data;
}

// Quanto dado está preso a este núcleo. O painel mostra ao administrador
// antes de ele confirmar a exclusão forçada — número concreto na tela
// pesa mais do que um aviso genérico de "isto é irreversível".
export async function contarVinculosNucleo(nucleoId) {
  const chamar = httpsCallable(functions, "contarVinculosNucleo");
  const r = await chamar({ nucleoId });
  return r.data;
}

// ── Ficha de cadastro de núcleo ──
// O levantamento que antecede a criação do núcleo: a coordenação geral
// informa instituição, canal de direitos e quem coordena; a coordenação
// técnica informa modalidades, categorias e locais de treino. Cada uma
// preenche por um link próprio, sem login, porque o coordenador técnico de
// um núcleo que ainda não existe não tem conta no sistema.
//
// Preencher não cria o núcleo. O administrador recebe, confere e cria.

export async function criarFichaDeNucleo(dados) {
  const chamar = httpsCallable(functions, "criarFichaDeNucleo");
  const r = await chamar(dados);
  return r.data;
}

export async function listarFichasDeNucleo() {
  const chamar = httpsCallable(functions, "listarFichasDeNucleo");
  const r = await chamar({});
  return r.data;
}

export async function atualizarFichaDeNucleo(dados) {
  const chamar = httpsCallable(functions, "atualizarFichaDeNucleo");
  const r = await chamar(dados);
  return r.data;
}

export async function criarNucleoDaFicha(fichaId, ajustes) {
  const chamar = httpsCallable(functions, "criarNucleoDaFicha");
  const r = await chamar({ fichaId, ajustes });
  return r.data;
}

// As duas de baixo rodam SEM login, na página pública da ficha. O token do
// link é a credencial, e é ele que decide qual das duas partes abre: o
// servidor nunca aceita a parte informada pelo endereço.
export async function abrirFichaDeNucleo(fichaId, token) {
  const chamar = httpsCallable(functions, "abrirFichaDeNucleo");
  const r = await chamar({ fichaId, token });
  return r.data;
}

export async function enviarFichaDeNucleo(fichaId, token, dados) {
  const chamar = httpsCallable(functions, "enviarFichaDeNucleo");
  const r = await chamar({ fichaId, token, dados });
  return r.data;
}

// O endereço que vai para o coordenador. Montado no portal, e não no
// servidor, porque é o portal que sabe em que site está rodando: o mesmo
// artefato serve produção e homologação.
export function linkDaFicha(fichaId, token) {
  const base = typeof window !== "undefined" && window.location
    ? window.location.origin
    : "";
  return `${base}/ficha?f=${encodeURIComponent(fichaId)}&t=${encodeURIComponent(token)}`;
}

// ── Treinos externos ──
// Local alternativo de treino, por modalidade e período. Cadastrado pelo
// técnico da modalidade (a coordenação também pode). Serve para os
// registros daqueles dias não gerarem aviso de "fora do local".
export async function carregarTreinosExternos(nucleo) {
  const q = query(collection(db, "treinos_externos"), where("nucleo", "==", nucleo));
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.dataInicio || "").localeCompare(a.dataInicio || ""));
}

export async function salvarTreinoExterno(dados) {
  const chamar = httpsCallable(functions, "salvarTreinoExterno");
  const r = await chamar(dados);
  return r.data;
}

export async function excluirTreinoExterno(treinoId) {
  const chamar = httpsCallable(functions, "excluirTreinoExterno");
  const r = await chamar({ treinoId });
  return r.data;
}

// Exclusão definitiva, só para a coordenação e só de treino já cancelado
// ou encerrado. O técnico cancela; apagar de vez remove o registro que
// explica por que presenças antigas daqueles dias não geraram aviso.
export async function apagarTreinoExterno(treinoId) {
  const chamar = httpsCallable(functions, "apagarTreinoExterno");
  const r = await chamar({ treinoId });
  return r.data;
}

// ── Solicitações de correção de falta (justificativa do atleta) ──
// O envio e a resolução passam por Cloud Functions (validação de prazo e
// permissão no servidor). A leitura é direta do Firestore, com as regras
// garantindo que o atleta vê só as dele e o coordenador as do núcleo.

// Atleta envia uma solicitação. A function valida o prazo (22h do 2º dia).
export async function criarSolicitacao({ dataTreino, justificativa, documentoUrl, documentoCaminho }) {
  const chamar = httpsCallable(functions, "criarSolicitacao");
  const r = await chamar({ dataTreino, justificativa, documentoUrl, documentoCaminho });
  return r.data;
}

// Coordenador aprova/nega. Aprovar cria a dispensa que abona a falta.
export async function resolverSolicitacao({ solicitacaoId, aprovar, resposta }) {
  const chamar = httpsCallable(functions, "resolverSolicitacao");
  const r = await chamar({ solicitacaoId, aprovar, resposta });
  // Aprovar cria a dispensa que abona a falta, ou o registro de presença:
  // o que estava guardado deixa de valer no mesmo instante, e a solicitação
  // sai da fila.
  esquecerLeiturasDePresenca();
  return r.data;
}

// Atleta: lê as próprias solicitações (para acompanhar o status).
// Os avisos de registro fora do local (origem "fora_do_local") ficam de
// fora: são pendências internas da coordenação, não pedidos que o atleta
// fez. Além de poluir a lista dele, apareceriam para o cálculo de
// "já solicitei nesta data" e o impediriam de enviar uma justificativa
// legítima para o mesmo dia.
export async function carregarMinhasSolicitacoes(uid) {
  const q = query(collection(db, "solicitacoes"), where("atletaUid", "==", uid));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }))
    .filter(s => s.origem !== "fora_do_local")
    .sort((a, b) => (b.dataTreino || "").localeCompare(a.dataTreino || ""));
}

// Ordena como a tela lê: pendentes primeiro, e dentro de cada grupo os
// mais recentes no topo.
function naOrdemDaTela(lista) {
  return lista.sort((a, b) => {
    if (a.status !== b.status) return a.status === "pendente" ? -1 : 1;
    return (b.dataTreino || "").localeCompare(a.dataTreino || "");
  });
}

// A FILA: o que espera decisão, de qualquer mês.
//
// Inteira de propósito, e é a única consulta aqui que não é por período:
// fila que esconde item deixa de ser fila, e um pedido de três meses atrás
// esquecido no fundo é exatamente o que não pode acontecer. São 3 hoje, e
// mesmo num mês ruim não passam de algumas dezenas.
export async function carregarSolicitacoesPendentes(nucleo) {
  if (!nucleo) return [];
  return cacheDeLeitura.obter(`solicitacoes|pendentes|${nucleo}`, async () => {
    const q = query(
      collection(db, "solicitacoes"),
      where("nucleo", "==", nucleo),
      where("status", "==", "pendente"),
    );
    const snap = await getDocs(q);
    return naOrdemDaTela(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

// O HISTÓRICO: só o mês pedido, e só quando pedido.
//
// São 95 solicitações hoje, 83 delas já resolvidas em meses fechados, e
// todas vinham em toda abertura da tela. O histórico é consulta, não fila:
// quem procura uma decisão antiga sabe de que mês ela é.
export async function carregarSolicitacoesDoPeriodo(nucleo, deISO, ateISO) {
  if (!nucleo || !deISO || !ateISO) return [];
  return cacheDeLeitura.obter(`solicitacoes|${nucleo}|${deISO}|${ateISO}`, async () => {
    const q = query(
      collection(db, "solicitacoes"),
      where("nucleo", "==", nucleo),
      where("dataTreino", ">=", deISO),
      where("dataTreino", "<=", ateISO),
    );
    const snap = await getDocs(q);
    return naOrdemDaTela(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

// mes é 0-11, como nos seletores das telas.
export async function carregarSolicitacoesDoMes(nucleo, mes, ano) {
  const periodo = intervaloDoMes(mes, ano);
  if (!periodo) return [];
  return carregarSolicitacoesDoPeriodo(nucleo, periodo.de, periodo.ate);
}

// A coleção inteira do núcleo. Sobrou para quem precisa mesmo de tudo, e
// hoje isso é o recuo da contagem de pendentes quando o count() é recusado.
export async function carregarSolicitacoesDoNucleo(nucleo) {
  const q = query(collection(db, "solicitacoes"), where("nucleo", "==", nucleo));
  const snap = await getDocs(q);
  return naOrdemDaTela(snap.docs.map(d => ({ id: d.id, ...d.data() })));
}

// Comprovantes/atestados anexados às solicitações de UMA modalidade do núcleo.
// Uso: consulta SÓ LEITURA para o técnico esportivo e o auxiliar da modalidade
// (não aprovam nada — isso é exclusivo dos coordenadores). Traz apenas
// solicitações que têm documento anexado, da modalidade informada, mais
// recentes primeiro.
//
// A modalidade NÃO entra na consulta ao Firestore, e sim no filtro em JS,
// porque a comparação lá é byte a byte. O cadastro real tem a mesma
// modalidade escrita de formas diferentes ("bmx" e "Bmx", "tenis_de_mesa"
// e "Tenis de Mesa"), então o filtro no servidor devolvia lista vazia
// para o técnico sempre que a grafia dele diferia da do atleta — e a aba
// de Justificativas aparecia vazia sem nenhum erro. normalizarModalidade
// resolve as variantes para o mesmo rótulo.
// Só pedidos de JUSTIFICATIVA, com documento anexado.
//
// O filtro é uma lista do que entra, e não do que sai. Antes ele apenas
// excluía "fora_do_local", e por isso a aba mostrava 28 fotos de registro
// de presença como se fossem atestados: todo registro pelo portal gera uma
// solicitação com documentoUrl, que é a selfie do momento.
//
// Lista do que entra: o pedido do atleta (gravado sem campo de origem) e a
// correção lançada pelo técnico. Qualquer origem nova nasce de fora desta
// aba, que é o lado certo para errar.
//
// O anexo NÃO entra no filtro, e já entrou: enquanto entrava, a aba mostrava
// 9 justificativas das 49 do núcleo, porque atestado é anexo e a maioria dos
// pedidos chega sem um — o atleta que faltou por motivo de família não tem o
// que anexar. Quem olha a aba precisa ver o pedido; o documento é detalhe do
// pedido, não a condição para ele existir.
function ehPedidoDeJustificativa(s) {
  return !s.origem || s.origem === "tecnico";
}

export async function carregarJustificativasDaModalidade(nucleo, modalidade, periodo) {
  const alvo = normalizarModalidade(modalidade);
  const todos = await carregarJustificativasDoNucleo(nucleo, periodo);
  return todos.filter(s => normalizarModalidade(s.modalidade) === alvo);
}

// Todas as modalidades do núcleo. A preparação física atende a turma
// inteira, então a justificativa de qualquer modalidade interessa a ela.
export async function carregarJustificativasDoNucleo(nucleo, periodo) {
  const lista = periodo && periodo.de && periodo.ate
    ? await carregarSolicitacoesDoPeriodo(nucleo, periodo.de, periodo.ate)
    : await carregarSolicitacoesDoNucleo(nucleo);
  return lista
    .filter(ehPedidoDeJustificativa)
    .sort((a, b) => (b.dataTreino || "").localeCompare(a.dataTreino || ""));
}

// Quantas solicitações esperam decisão.
//
// O selo do menu e a fila da tela de início mostram um número, e para isso
// baixavam as 95 solicitações do núcleo, duas vezes, a cada abertura. O
// count() do Firestore cobra uma leitura por até mil documentos: 95 viram 1,
// e continua 1 quando forem 900.
export async function contarSolicitacoesPendentes(nucleo) {
  if (!nucleo) return 0;
  const q = query(
    collection(db, "solicitacoes"),
    where("nucleo", "==", nucleo),
    where("status", "==", "pendente"),
  );
  try {
    const snap = await getCountFromServer(q);
    return snap.data().count;
  } catch (e) {
    // Se a contagem no servidor for recusada por regra ou faltar índice, o
    // selo do menu não pode sumir: volta ao caminho antigo, que custa mais
    // e funciona. Silenciar o erro aqui esconderia um problema; deixar o
    // número zerado esconderia uma fila de pedidos esperando decisão.
    console.warn("contagem no servidor indisponível, lendo a lista:", e.message);
    const lista = await carregarSolicitacoesDoNucleo(nucleo);
    return lista.filter((x) => (x.status || "pendente") === "pendente").length;
  }
}

// ── Decisões do técnico esperando a ciência da coordenação ──
//
// O que o técnico resolve sai da fila de pendentes e, sem isto, sumia da
// vista da coordenação: a delegação acontecia no escuro e o poder de rever,
// que é dela, dependia de ela ir procurar no histórico.
//
// Duas igualdades, sem ordenação: o Firestore resolve com os índices de
// campo único, sem índice composto.
export async function carregarDecisoesParaCiencia(nucleo) {
  if (!nucleo) return [];
  return cacheDeLeitura.obter(`ciencia|${nucleo}`, async () => {
    const q = query(
      collection(db, "solicitacoes"),
      where("nucleo", "==", nucleo),
      where("aguardaCiencia", "==", true),
    );
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => String(b.dataTreino || "").localeCompare(String(a.dataTreino || "")));
  });
}

// Tira o aviso da fila. Não muda o que foi decidido: para desfazer existe
// reverterSolicitacao, que exige motivo.
export async function darCienciaNaDecisao(ids) {
  const lista = Array.isArray(ids) ? ids : [ids];
  const chamar = httpsCallable(functions, "darCienciaNaDecisao");
  const r = await chamar({ solicitacaoIds: lista });
  cacheDeLeitura.invalidar();
  return r.data;
}

// Coordenação reverte uma solicitação resolvida (desfaz dispensa + volta a
// pendente). O motivo é obrigatório: reverter desfaz uma decisão que já foi
// comunicada ao atleta, e o histórico precisa guardar por quê.
export async function reverterSolicitacao({ solicitacaoId, motivo }) {
  const chamar = httpsCallable(functions, "reverterSolicitacao");
  const r = await chamar({ solicitacaoId, motivo });
  esquecerLeiturasDePresenca();
  return r.data;
}

// Devolve a solicitação ao atleta pedindo o que faltou, sem negar: negar
// por falta de anexo gasta uma das três tentativas dele com um erro de
// preenchimento que ele já sabe corrigir.
export async function solicitarComplemento({ solicitacaoId, mensagem }) {
  const chamar = httpsCallable(functions, "solicitarComplemento");
  const r = await chamar({ solicitacaoId, mensagem });
  return r.data;
}

// ── Upload de comprovante de justificativa (3B-2) ──
// Sobe o arquivo para justificativas/{nucleo}/{uid}/{timestamp}_{nome} e
// devolve a URL de download. As regras do Storage garantem que só o próprio
// atleta escreve na sua pasta (imagem/PDF até 5MB) e que só ele e a
// coordenação do núcleo leem. Valida tipo/tamanho no cliente também (UX).
export async function enviarComprovante({ uid, nucleo, arquivo }) {
  if (!arquivo) throw new Error("Nenhum arquivo selecionado.");
  const tiposOk = ["image/jpeg", "image/png", "image/jpg", "image/webp", "application/pdf"];
  if (!tiposOk.includes(arquivo.type)) {
    throw new Error("Envie uma imagem (JPG, PNG) ou um PDF.");
  }
  if (arquivo.size > 5 * 1024 * 1024) {
    throw new Error("Arquivo muito grande (máximo 5MB).");
  }
  // Nome seguro: timestamp + nome original sem caracteres problemáticos.
  const nomeSeguro = `${Date.now()}_${arquivo.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
  const caminho = `justificativas/${nucleo}/${uid}/${nomeSeguro}`;
  const r = storageRef(storage, caminho);
  await uploadBytes(r, arquivo);
  const url = await getDownloadURL(r);
  return { url, caminho };
}

// Técnico solicita correção de presença de um atleta da modalidade dele.
export async function criarSolicitacaoTecnico({ atletaUid, dataTreino, justificativa }) {
  const chamar = httpsCallable(functions, "criarSolicitacaoTecnico");
  const r = await chamar({ atletaUid, dataTreino, justificativa });
  return r.data;
}

// ── Registro de presença pelo portal (3C) ──
// Sobe a foto (da câmera) para registros_portal/ e devolve a URL.
export async function enviarFotoRegistro({ uid, nucleo, blob }) {
  if (!blob) throw new Error("Foto não capturada.");
  if (blob.size > 5 * 1024 * 1024) throw new Error("Foto muito grande (máximo 5MB).");
  const nomeSeguro = `${Date.now()}.jpg`;
  const caminho = `registros_portal/${nucleo}/${uid}/${nomeSeguro}`;
  const r = storageRef(storage, caminho);
  await uploadBytes(r, blob);
  const url = await getDownloadURL(r);
  return { url, caminho };
}

// Cria a solicitação de presença pelo portal (foto já enviada + GPS).
export async function registrarPresencaPortal({ fotoUrl, fotoCaminho, lat, lng, precisaoMetros, nomeDoLugar }) {
  const chamar = httpsCallable(functions, "registrarPresencaPortal");
  const r = await chamar({ fotoUrl, fotoCaminho, lat, lng, precisaoMetros, nomeDoLugar });
  esquecerLeiturasDePresenca();
  return r.data;
}

// Equipe técnica registra a própria presença pelo portal (direto, sem aprovação).
export async function registrarPresencaEquipe({ fotoUrl, fotoCaminho, lat, lng, precisaoMetros, nomeDoLugar }) {
  const chamar = httpsCallable(functions, "registrarPresencaEquipe");
  const r = await chamar({ fotoUrl, fotoCaminho, lat, lng, precisaoMetros, nomeDoLugar });
  esquecerLeiturasDePresenca();
  return r.data;
}

// ══════════════════════════════════════════════════════════════
// PRESENÇA FÍSICA (módulo do preparador) — informativo, separado
// do cálculo de frequência oficial. Não toca em attendance_logs
// nem em calculo.js.
// ══════════════════════════════════════════════════════════════

// Chave canônica do doc de config: núcleo + modalidade normalizada.
// Ex: "IFCE__bmx". Isolado por núcleo, como o resto do sistema.
function chaveTreinoFisico(nucleo, modalidade) {
  const mod = normalizarModalidade(modalidade || "").toLowerCase().replace(/\s+/g, "_");
  return `${nucleo}__${mod}`;
}

// Calendário semanal de treino FÍSICO de uma modalidade num núcleo.
// diasSemana: array de números 1..5 (segunda..sexta). Vazio = sem treino
// físico configurado (padrão: fechado). Perdura até o preparador alterar.
// Treino físico do PRÓPRIO atleta.
//
// A regra do Firestore só deixa o atleta ler o que é dele, e o Firestore
// recusa a consulta inteira se ela puder devolver documento não
// autorizado — então o filtro precisa estar NA CONSULTA, não depois.
//
// São duas consultas porque os registros criados antes da correção não
// têm atletaUid; para esses, o e-mail é a única âncora. Juntamos sem
// duplicar, como já é feito com as dispensas do atleta.
export async function carregarMinhaPresencaFisica(nucleo, uid, email, mesPrefixo) {
  const base = collection(db, "presenca_fisica");
  const consultas = [];
  if (uid) consultas.push(query(base, where("nucleo", "==", nucleo), where("atletaUid", "==", uid)));
  if (email) consultas.push(query(base, where("nucleo", "==", nucleo), where("atletaEmail", "==", email.toLowerCase())));

  const partes = await Promise.all(consultas.map(async c => {
    try { return (await getDocs(c)).docs; } catch { return []; }
  }));

  const mapa = {};
  partes.flat().forEach(d => {
    const x = d.data();
    if (mesPrefixo && !(x.data || "").startsWith(mesPrefixo)) return;
    mapa[x.data] = x.status;   // { "2026-08-14": "presente" }
  });
  return mapa;
}

export async function carregarDiasTreinoFisico(nucleo) {
  const q = query(collection(db, "treino_fisico_config"), where("nucleo", "==", nucleo));
  const snap = await getDocs(q);
  const mapa = {}; // { modalidadeNormalizadaLower: [1,3,5] }
  snap.forEach(d => {
    const dados = d.data();
    const mod = normalizarModalidade(dados.modalidade || "").toLowerCase();
    mapa[mod] = Array.isArray(dados.diasSemana) ? dados.diasSemana : [];
  });
  return mapa;
}

// Salva o calendário semanal de uma modalidade. Só preparador/auxiliar
// (garantido também nas regras do Firestore).
export async function salvarDiasTreinoFisico({ nucleo, modalidade, diasSemana }) {
  const id = chaveTreinoFisico(nucleo, modalidade);
  await setDoc(doc(db, "treino_fisico_config", id), {
    nucleo,
    modalidade: normalizarModalidade(modalidade || ""),
    diasSemana: (diasSemana || []).filter(n => n >= 1 && n <= 5).sort(),
    atualizadoEm: serverTimestamp(),
    atualizadoPor: auth.currentUser ? auth.currentUser.uid : null,
  }, { merge: true });
  return { ok: true };
}

// Marca presença/falta FÍSICA de um atleta num dia. Coleção própria,
// informativa. Um doc por atleta+dia. status: "presente" | "falta".
export async function salvarPresencaFisica({ nucleo, modalidade, atletaUid, atletaNome, atletaEmail, data, status }) {
  const modNorm = normalizarModalidade(modalidade || "").toLowerCase().replace(/\s+/g, "_");
  // A chave do documento é o NOME, e precisa continuar sendo.
  //
  // Os registros já gravados usam o nome ("IFCE__bmx__adrian_dastan__2026-08-03").
  // Passar a usar o e-mail geraria um documento paralelo por atleta e dia,
  // e as marcações antigas sumiriam da grade. atletaUid e atletaEmail vão
  // como CAMPOS — é deles que a regra do Firestore precisa para o atleta
  // ler o próprio histórico, não da chave.
  const chaveNome = (atletaNome || "").toLowerCase().replace(/[^a-z0-9]/g, "_");
  const id = `${nucleo}__${modNorm}__${chaveNome}__${data}`;
  await setDoc(doc(db, "presenca_fisica", id), {
    nucleo,
    modalidade: normalizarModalidade(modalidade || ""),
    atletaUid: atletaUid || null,
    atletaNome: atletaNome || "",
    atletaEmail: atletaEmail || "",
    data,
    status, // "presente" | "falta"
    marcadoEm: serverTimestamp(),
    marcadoPor: auth.currentUser ? auth.currentUser.uid : null,
  }, { merge: true });
  return { ok: true };
}

// Carrega a presença física de um núcleo num mês (para montar a grade).
// Retorna um mapa { "email__data": "presente"|"falta" }.
export async function carregarPresencaFisica(nucleo, mesPrefixo) {
  const q = query(collection(db, "presenca_fisica"), where("nucleo", "==", nucleo));
  const snap = await getDocs(q);
  const mapa = {};
  snap.forEach(d => {
    const x = d.data();
    if (mesPrefixo && !(x.data || "").startsWith(mesPrefixo)) return;
    // Indexado pelo NOME, igual à chave do documento e à que a tela monta.
    // Antes tentava o e-mail primeiro; como os registros existentes têm
    // atletaEmail vazio, caía no nome — mas quando o e-mail passou a vir
    // preenchido os dois lados divergiram e a grade ficou vazia.
    const chaveNome = (x.atletaNome || "").toLowerCase().replace(/[^a-z0-9]/g, "_");
    mapa[`${chaveNome}__${x.data}`] = x.status;
  });
  return mapa;
}

// ── Cadastro facial ─────────────────────────────────────────
// Tudo passa pelo servidor: os arquivos ficam num caminho que nenhuma regra
// do Storage libera, e vêm em bytes por chamada autenticada. Biometria de
// menor não pode ter link que sobreviva fora da sessão.
export async function listarCadastroFacial(nucleo) {
  const chamar = httpsCallable(functions, "listarCadastroFacial");
  const r = await chamar({ nucleo });
  return r.data;
}

export async function obterArquivoBiometrico({ alunoUid, qual }) {
  const chamar = httpsCallable(functions, "obterArquivoBiometrico");
  const r = await chamar({ alunoUid, qual });
  return r.data;
}

// `estado` é o que o responsável marcou no termo: autorizado ou recusado.
// O servidor recusa a chamada sem ele, de propósito.
export async function salvarConsentimentoRosto({ alunoUid, arquivoBase64, tipoArquivo, estado }) {
  const chamar = httpsCallable(functions, "salvarConsentimentoRosto");
  const r = await chamar({ alunoUid, arquivoBase64, tipoArquivo, estado });
  return r.data;
}

// Marca a recusa do responsável. Não leva arquivo: o termo impresso só
// tem a caixa de AUTORIZO, e a recusa acontece quando o papel volta sem
// marca nenhuma ou a família avisa que não quer.
export async function recusarConsentimentoRosto({ alunoUid, observacao }) {
  const chamar = httpsCallable(functions, "recusarConsentimentoRosto");
  const r = await chamar({ alunoUid, observacao });
  return r.data;
}

export async function salvarFotoReferencia({ alunoUid, fotoBase64, tipoArquivo }) {
  const chamar = httpsCallable(functions, "salvarFotoReferencia");
  const r = await chamar({ alunoUid, fotoBase64, tipoArquivo });
  return r.data;
}

export async function removerFotoReferencia(alunoUid) {
  const chamar = httpsCallable(functions, "removerFotoReferencia");
  const r = await chamar({ alunoUid });
  return r.data;
}

export async function removerConsentimentoRosto(alunoUid) {
  const chamar = httpsCallable(functions, "removerConsentimentoRosto");
  const r = await chamar({ alunoUid });
  return r.data;
}

// ── Foto de cadastro ────────────────────────────────────────
// O retrato de uniforme da lista de atletas, que NÃO é a foto de referência
// do reconhecimento facial. São duas fotos com finalidades diferentes, e o
// servidor as guarda em pastas diferentes de propósito: usar o retrato como
// referência biométrica trocaria o consentimento de uma finalidade pelo de
// outra.
//
// Vem em bytes por chamada autenticada, como a biométrica. Não é dado
// biométrico, mas continua sendo a imagem de um menor, e link de imagem de
// menor sobrevive à sessão.
export async function salvarFotoDeCadastro({ alunoUid, fotoBase64, tipoArquivo }) {
  const chamar = httpsCallable(functions, "salvarFotoDeCadastro");
  const r = await chamar({ alunoUid, fotoBase64, tipoArquivo });
  return r.data;
}

export async function obterFotoDeCadastro(alunoUid) {
  const chamar = httpsCallable(functions, "obterFotoDeCadastro");
  const r = await chamar({ alunoUid });
  return r.data;
}

export async function removerFotoDeCadastro(alunoUid) {
  const chamar = httpsCallable(functions, "removerFotoDeCadastro");
  const r = await chamar({ alunoUid });
  return r.data;
}

// ── Planejamento trimestral ─────────────────────────────────
export async function listarPlanejamento(nucleo, trimestreId) {
  const chamar = httpsCallable(functions, "listarPlanejamento");
  const r = await chamar({ nucleo, trimestreId });
  return r.data;
}

export async function removerTrimestre(trimestreId) {
  const chamar = httpsCallable(functions, "removerTrimestre");
  const r = await chamar({ trimestreId });
  return r.data;
}

export async function salvarTrimestre(dados) {
  const chamar = httpsCallable(functions, "salvarTrimestre");
  const r = await chamar(dados);
  return r.data;
}

export async function salvarPlano({ trimestreId, tipo, modalidade, conteudo }) {
  const chamar = httpsCallable(functions, "salvarPlano");
  const r = await chamar({ trimestreId, tipo, modalidade, conteudo });
  return r.data;
}

export async function enviarPlano({ trimestreId, tipo, modalidade, conteudo }) {
  const chamar = httpsCallable(functions, "enviarPlano");
  const r = await chamar({ trimestreId, tipo, modalidade, conteudo });
  return r.data;
}

// O atleta tira outra foto quando a primeira não conferiu.
//
// A foto viaja na chamada e não é guardada em lugar nenhum: a prova da
// presença continua sendo a foto do registro, e uma segunda imagem de menor
// de idade guardada por conferência seria dado novo sem uso novo.
export async function reconferirRosto({ registroId, fotoBase64, tipoArquivo }) {
  const chamar = httpsCallable(functions, "reconferirRosto");
  const r = await chamar({ registroId, fotoBase64, tipoArquivo });
  return r.data;
}

// Em que pé está a conferência facial: se a credencial da AWS é aceita,
// se a conferência está ligada e quantos atletas já têm foto. A pergunta
// sobre a credencial é uma chamada real ao serviço, e não a leitura de um
// campo preenchido — campo preenchido com o valor errado tem exatamente a
// mesma aparência de campo certo.
export async function estadoDoReconhecimento(nucleo) {
  const chamar = httpsCallable(functions, "estadoDoReconhecimento");
  const r = await chamar({ nucleo });
  return r.data;
}

// ── Planilha de treino da preparação física ─────────────────
export async function listarPlanilhasTreino({ nucleo, anoMes } = {}) {
  const chamar = httpsCallable(functions, "listarPlanilhasTreino");
  const r = await chamar({ nucleo, anoMes });
  return r.data;
}

export async function salvarPlanilhaTreino({ modalidade, anoMes, alvo, alvoNome, conteudo }) {
  const chamar = httpsCallable(functions, "salvarPlanilhaTreino");
  const r = await chamar({ modalidade, anoMes, alvo, alvoNome, conteudo });
  return r.data;
}

export async function resolverPlano({ planoId, aprovar, observacoes }) {
  const chamar = httpsCallable(functions, "resolverPlano");
  const r = await chamar({ planoId, aprovar, observacoes });
  return r.data;
}

export async function meusAvisosDePlano() {
  const chamar = httpsCallable(functions, "meusAvisosDePlano");
  const r = await chamar({});
  return r.data;
}

// ── Competições e resultados ────────────────────────────────
export async function listarCompeticoes(nucleo, modalidade) {
  const chamar = httpsCallable(functions, "listarCompeticoes");
  const r = await chamar({ nucleo, modalidade });
  return r.data;
}

// A agenda do que ainda vai acontecer, para quem não lança resultado.
//
// Leitura separada de `listarCompeticoes` de propósito: aquela é a tela de
// lançamento, devolve atletas e resultados e é restrita à coordenação e à
// equipe da modalidade. Esta devolve só a agenda, e a preparação física
// precisa dela para decidir a carga da semana.
export async function listarProximasCompeticoes() {
  const chamar = httpsCallable(functions, "listarProximasCompeticoes");
  const r = await chamar({});
  return r.data;
}

export async function salvarCompeticao(dados) {
  const chamar = httpsCallable(functions, "salvarCompeticao");
  const r = await chamar(dados);
  return r.data;
}

export async function salvarResultados({ competicaoId, resultados }) {
  const chamar = httpsCallable(functions, "salvarResultados");
  const r = await chamar({ competicaoId, resultados });
  return r.data;
}

export async function removerCompeticao(competicaoId) {
  const chamar = httpsCallable(functions, "removerCompeticao");
  const r = await chamar({ competicaoId });
  return r.data;
}

export async function rankingDeMedalhas({ nucleo, modalidade, de, ate }) {
  const chamar = httpsCallable(functions, "rankingDeMedalhas");
  const r = await chamar({ nucleo, modalidade, de, ate });
  return r.data;
}

// ── Avaliação de encerramento do ciclo ───────────────────────
//
// Uma resposta por atleta por ciclo, e o id do documento diz as duas
// coisas: `2026_<uid>`. Com o id previsível, saber se o atleta já
// respondeu custa UMA leitura por id, e não uma consulta à coleção. Isso
// importa porque essa pergunta é feita em toda entrada de atleta no
// portal, que é o momento de maior tráfego do painel.
//
// O id também é o que segura a regra do Firestore: ela confere que o
// trecho depois do ciclo é o uid de quem está gravando, então ninguém
// responde no lugar de outro, nem responde duas vezes.
export function idDaAvaliacao(ciclo, uid) {
  return `${ciclo}_${uid}`;
}

export async function carregarMinhaAvaliacaoDoCiclo(uid, ciclo) {
  const snap = await getDoc(doc(db, "avaliacoes_ciclo", idDaAvaliacao(ciclo, uid)));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// Grava a resposta. Sem merge: o documento nasce completo e não é
// reescrito depois (a regra só permite criar), então o painel nunca fica
// com meia avaliação gravada.
//
// `idadeNaResposta` é a única idade gravada em todo o sistema, e é de
// propósito. Em toda outra tela a idade é calculada da data de
// nascimento, porque idade guardada envelhece errado. Aqui ela está presa
// a `enviadoEm`: é a idade que o atleta tinha quando respondeu, que é
// exatamente o que o questionário de papel coletava, e é o que permite
// ler as respostas por faixa de idade daqui a dois ciclos.
export async function enviarAvaliacaoDoCiclo({
  uid, ciclo, nome, email, nucleo, modalidade, idadeNaResposta, respostas, noPrazo,
}) {
  const id = idDaAvaliacao(ciclo, uid);
  await setDoc(doc(db, "avaliacoes_ciclo", id), {
    ciclo: String(ciclo),
    uid,
    nome: nome || "",
    email: email || "",
    nucleo: nucleo || "",
    modalidade: modalidade || "",
    idadeNaResposta: typeof idadeNaResposta === "number" ? idadeNaResposta : null,
    respostas: respostas || {},
    noPrazo: noPrazo === true,
    enviadoEm: serverTimestamp(),
  });
  return id;
}

// As avaliações de um ciclo, para a coordenação ler e exportar.
//
// Filtra por núcleo e ciclo no servidor, e não baixa a coleção inteira
// para separar no navegador: a coleção cresce um documento por atleta por
// ciclo e nunca encolhe, então a leitura do ciclo passado ficaria mais
// cara a cada ano que passa.
export async function carregarAvaliacoesDoCiclo(nucleo, ciclo) {
  const q = query(
    collection(db, "avaliacoes_ciclo"),
    where("nucleo", "==", nucleo),
    where("ciclo", "==", String(ciclo)),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));
}

// A presença do treino físico de UM mês, pedida por intervalo de datas.
//
// A carregarPresencaFisica acima baixa o histórico inteiro do núcleo e
// separa o mês no navegador. Ela continua assim porque a grade de presença
// já contava com isso; aqui, que é consulta de apoio dentro da planilha de
// treino, o mês é pedido ao servidor: a coleção cresce um documento por
// atleta por dia e nunca encolhe, e esta tela seria mais uma a pagar o
// histórico todo para mostrar quatro segundas-feiras.
//
// Devolve { "<nome em chave>__<data>": "presente" | "falta" }, indexado
// pelo NOME como a grade faz, porque os registros antigos não têm uid.
export async function carregarPresencaFisicaDoMes(nucleo, anoMes) {
  if (!nucleo || !/^\d{4}-\d{2}$/.test(String(anoMes || ""))) return {};
  const q = query(
    collection(db, "presenca_fisica"),
    where("nucleo", "==", nucleo),
    where("data", ">=", `${anoMes}-01`),
    where("data", "<=", `${anoMes}-31`),
  );
  const snap = await getDocs(q);
  const mapa = {};
  snap.forEach((d) => {
    const x = d.data();
    const chaveNome = (x.atletaNome || "").toLowerCase().replace(/[^a-z0-9]/g, "_");
    mapa[`${chaveNome}__${x.data}`] = x.status;
  });
  return mapa;
}

// ── Avaliação física e antropométrica ────────────────────────
//
// Uma coleta por documento, com id previsível `<uid>__<data>`. Previsível
// porque a coleta é identificada pelo atleta e pelo dia, e gravar duas
// vezes o mesmo dia tem de corrigir a primeira, não criar uma segunda: o
// avaliador que erra uma dobra reabre a tela e salva de novo, e dois
// documentos para o mesmo dia virariam dois pontos na série.
//
// Os números ficam crus, do jeito que saíram do adipômetro e da fita.
// Percentual de gordura, somatotipo e maturação NÃO são gravados, são
// calculados na leitura por `avaliacaoFisica.js`. É o que permite
// corrigir uma equação ou trocar a conversão de densidade sem reabrir
// trinta documentos, e é o que garante que a tela, o Excel e o PDF
// mostrem o mesmo número.
export function idDaAvaliacaoFisica(uid, data) {
  return `${uid}__${data}`;
}

export async function carregarAvaliacoesFisicas(nucleo) {
  const q = query(collection(db, "avaliacoes_fisicas"), where("nucleo", "==", nucleo));
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(a.data || "").localeCompare(String(b.data || "")));
}

export async function salvarAvaliacaoFisica(registro) {
  const { atletaUid, data } = registro;
  if (!atletaUid || !data) throw new Error("avaliação física sem atleta ou sem data");
  const id = idDaAvaliacaoFisica(atletaUid, data);
  await setDoc(
    doc(db, "avaliacoes_fisicas", id),
    { ...registro, atualizadoEm: serverTimestamp() },
    { merge: true },
  );
  return id;
}

export async function apagarAvaliacaoFisica(atletaUid, data) {
  await deleteDoc(doc(db, "avaliacoes_fisicas", idDaAvaliacaoFisica(atletaUid, data)));
}

// ── Avaliação técnica e testes físicos ───────────────────────
//
// As duas seguem exatamente o desenho de `avaliacoes_fisicas` acima, e a
// repetição é proposital: três coleções com o mesmo id previsível e a
// mesma regra de merge são três coisas que se conferem lendo uma só.
//
// Id `<uid>__<data>`: a avaliação é identificada pelo atleta e pelo dia,
// e salvar de novo o mesmo dia corrige a primeira em vez de criar um
// segundo ponto na série.
//
// ── O que é gravado, e o que não é ──────────────────────────
//
// Gravado: a nota de cada critério, os tempos cronometrados, a lista de
// critérios daquele dia e o divisor usado. Não gravado: somatório,
// média, faixa da escala, escore global e classificação, que saem do
// cálculo na leitura, em `avaliacaoTecnica.js`, `testesFisicos.js` e
// `desempenho.js`.
//
// A exceção é a escala de tempo do BMX, que É gravada mesmo sendo
// derivada. Ela não é resultado de fórmula sobre o documento, é a régua
// do GRUPO naquele dia: recalculá-la na leitura mudaria a nota de agosto
// de 2026 no dia em que um atleta novo entrasse na turma.
export function idDaAvaliacaoTecnica(uid, data) {
  return `${uid}__${data}`;
}

export async function carregarAvaliacoesTecnicas(nucleo) {
  const q = query(collection(db, "avaliacoes_tecnicas"), where("nucleo", "==", nucleo));
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(a.data || "").localeCompare(String(b.data || "")));
}

export async function salvarAvaliacaoTecnica(registro) {
  const { atletaUid, data } = registro;
  if (!atletaUid || !data) throw new Error("avaliação técnica sem atleta ou sem data");
  const id = idDaAvaliacaoTecnica(atletaUid, data);
  await setDoc(
    doc(db, "avaliacoes_tecnicas", id),
    { ...registro, atualizadoEm: serverTimestamp() },
    { merge: true },
  );
  return id;
}

export async function apagarAvaliacaoTecnica(atletaUid, data) {
  await deleteDoc(doc(db, "avaliacoes_tecnicas", idDaAvaliacaoTecnica(atletaUid, data)));
}

// ── Os critérios de cada modalidade, por núcleo ──────────────
//
// O que fica gravado aqui é a DIFERENÇA em relação ao modelo que está no
// código: os critérios que a comissão acrescentou e os que ela deixou de
// avaliar. Nunca a lista inteira. O motivo está em `avaliacaoTecnica.js`,
// em `criteriosDaModalidade`: guardando a diferença, uma melhoria no
// modelo chega a todos os núcleos, e guardando a cópia não chegaria a
// nenhum que já tivesse mexido nos critérios uma vez.
//
// Isto NÃO muda avaliação já gravada. Cada avaliação carrega a lista de
// critérios dela dentro do documento, e continua compilando com ela.
// Mexer aqui muda as PRÓXIMAS coletas, que é o que a coordenação pediu
// em 22/10/2026 ao querer acrescentar critérios "para inclusão nas
// próximas avaliações".
function chaveCriteriosTecnicos(nucleo, modalidade) {
  const mod = normalizarModalidade(modalidade || "").toLowerCase().replace(/\s+/g, "_");
  return `${nucleo}__${mod}`;
}

export async function carregarCriteriosTecnicos(nucleo) {
  const q = query(collection(db, "criterios_tecnicos"), where("nucleo", "==", nucleo));
  const snap = await getDocs(q);
  const mapa = {}; // { "Tênis de Mesa": { extras: [...], desativados: [...] } }
  snap.forEach(d => {
    const dados = d.data();
    if (!dados.modalidade) return;
    mapa[dados.modalidade] = {
      extras: Array.isArray(dados.extras) ? dados.extras : [],
      desativados: Array.isArray(dados.desativados) ? dados.desativados : [],
      atualizadoEm: dados.atualizadoEm || null,
    };
  });
  return mapa;
}

export async function salvarCriteriosTecnicos({ nucleo, modalidade, extras, desativados }) {
  if (!nucleo || !modalidade) throw new Error("critérios sem núcleo ou sem modalidade");
  const id = chaveCriteriosTecnicos(nucleo, modalidade);
  await setDoc(doc(db, "criterios_tecnicos", id), {
    nucleo,
    // A modalidade vai como o painel a escreve, e não normalizada, porque
    // é por ela que a tela casa a configuração com o atleta.
    modalidade,
    extras: (Array.isArray(extras) ? extras : []).map(c => {
      const saida = { id: c.id, rotulo: c.rotulo };
      if (c.grupo) saida.grupo = c.grupo;
      if (c.detalhe) saida.detalhe = c.detalhe;
      return saida;
    }),
    desativados: (Array.isArray(desativados) ? desativados : []).filter(Boolean),
    atualizadoEm: serverTimestamp(),
    atualizadoPor: auth.currentUser ? auth.currentUser.uid : null,
  }, { merge: true });
  return id;
}

export function idDoTesteFisico(uid, data) {
  return `${uid}__${data}`;
}

export async function carregarTestesFisicos(nucleo) {
  const q = query(collection(db, "testes_fisicos"), where("nucleo", "==", nucleo));
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(a.data || "").localeCompare(String(b.data || "")));
}

export async function salvarTesteFisico(registro) {
  const { atletaUid, data } = registro;
  if (!atletaUid || !data) throw new Error("teste físico sem atleta ou sem data");
  const id = idDoTesteFisico(atletaUid, data);
  await setDoc(
    doc(db, "testes_fisicos", id),
    { ...registro, atualizadoEm: serverTimestamp() },
    { merge: true },
  );
  return id;
}

export async function apagarTesteFisico(atletaUid, data) {
  await deleteDoc(doc(db, "testes_fisicos", idDoTesteFisico(atletaUid, data)));
}
